// Base step wrapper component for consistent styling and animations

import React from 'react';
import { motion } from 'framer-motion';
import { Info, Loader2 } from 'lucide-react';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SectionHelp } from './WizardTooltip';

interface WizardStepProps {
  title: string;
  description?: string;
  helpText?: string;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
}

export function WizardStep({
  title,
  description,
  helpText,
  children,
  className,
  contentClassName
}: WizardStepProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={className}
    >
      <CardHeader className="px-4 sm:px-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-xl sm:text-2xl">{title}</CardTitle>
            {description && (
              <CardDescription className="mt-2 text-sm sm:text-base">{description}</CardDescription>
            )}
          </div>
          {helpText && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="ml-4 p-1 rounded-full hover:bg-gray-100 transition-colors touch-target"
                  >
                    <Info className="w-5 h-5 text-gray-400" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p className="text-sm">{helpText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </CardHeader>
      <CardContent className={cn("space-y-6 px-4 sm:px-6", contentClassName)}>
        {children}
      </CardContent>
    </motion.div>
  );
}

// Variant for steps with visual selections (like style or color selection)
export function VisualSelectionStep({
  title,
  description,
  helpText,
  children,
  className,
  contentClassName
}: WizardStepProps) {
  return (
    <WizardStep
      title={title}
      description={description}
      helpText={helpText}
      className={className}
      contentClassName={cn("pb-8", contentClassName)}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        {children}
      </motion.div>
    </WizardStep>
  );
}

// Loading state for steps
export function WizardStepLoading({ message = "Loading..." }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      >
        <Loader2 className="w-8 h-8 text-purple-600" />
      </motion.div>
      <p className="mt-4 text-gray-600">{message}</p>
    </div>
  );
}

// Error state for steps
export function WizardStepError({ 
  message = "Something went wrong", 
  onRetry 
}: { 
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
        <span className="text-red-600 text-xl">!</span>
      </div>
      <p className="text-gray-700 font-medium">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-4 text-purple-600 hover:text-purple-700 underline"
        >
          Try again
        </button>
      )}
    </div>
  );
}
